
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ContentItem } from '@/components/dashboard/content-card/types';
import { useAuth } from '@/contexts/AuthContext';
import { Tier1Tracking } from '@/services/analytics/Tier1Tracking';

interface SearchContextType {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: ContentItem[];
  isSearching: boolean;
  isSearchOpen: boolean;
  setIsSearchOpen: (open: boolean) => void;
  performSearch: (query: string) => void;
  trackSearchResultClick: (contentItem: ContentItem) => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

interface SearchProviderProps {
  children: React.ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { user } = useAuth();

  // Track search performance
  const performSearch = useCallback((query: string) => {
    setSearchQuery(query);
    
    // Track the search event
    if (query.trim() && user) {
      Tier1Tracking.trackEvent('search_performed', 'discovery', {
        query: query.trim(),
        query_length: query.trim().length
      }, user);
    }
  }, [user]);

  // Track when user clicks on a search result
  const trackSearchResultClick = useCallback((contentItem: ContentItem) => {
    if (user && searchQuery.trim()) {
      Tier1Tracking.trackEvent('search_result_clicked', 'discovery', {
        content_id: contentItem.id,
        content_title: contentItem.title,
        search_query: searchQuery.trim(),
        content_type: contentItem.content_type
      }, user);
    }
  }, [user, searchQuery]);

  const { data: searchResults = [], isLoading: isSearching } = useQuery({
    queryKey: ['search', searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim()) return [];
      
      const { data, error } = await supabase.rpc('search_content', {
        search_query: searchQuery.trim()
      });

      if (error) {
        console.error('Search error:', error);
        return [];
      }

      // Transform the data to match ContentItem interface by adding missing properties
      const transformedData = (data || []).map((item: any) => ({
        ...item,
        archived: item.archived || false, // Add default archived property
        notes: item.notes || null, // Add notes property if missing
        collection_color: item.collection_color || null // Ensure collection_color is present
      }));

      return transformedData;
    },
    enabled: !!searchQuery.trim(),
    staleTime: 30000, // 30 seconds
  });

  return (
    <SearchContext.Provider
      value={{
        searchQuery,
        setSearchQuery,
        searchResults,
        isSearching,
        isSearchOpen,
        setIsSearchOpen,
        performSearch,
        trackSearchResultClick,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
